/**
 * Consultation Dialog - Form for creating/editing consultations
 * Note: Appointment scheduling removed - use Calendar page instead
 */
import React, { useState } from 'react';
import { format } from 'date-fns';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Stethoscope, Upload, X, Sun, Sunset, Moon, Calendar, FileText, Loader2, Check } from 'lucide-react';
import { DiagnosisSelector } from './DiagnosisSelector';
import { MedicationSelector } from './MedicationSelector';
import { DatePicker } from '../ui/date-picker';
import { getInitialMedication } from './utils';
import { extractLabValues } from '../../services/api';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

export const ConsultationDialog = ({
  open,
  onOpenChange,
  isEditing,
  consultationForm,
  setConsultationForm,
  diagnoses,
  medications,
  uploadingFiles,
  setUploadingFiles,
  onSubmit
}) => {
  const [diagnosisSearch, setDiagnosisSearch] = useState('');
  const [medicationSearch, setMedicationSearch] = useState('');
  const [newMedication, setNewMedication] = useState(getInitialMedication());
  const [extracting, setExtracting] = useState(false);
  const [extractedFields, setExtractedFields] = useState([]);

  // Reset extractedFields when dialog opens/closes
  React.useEffect(() => {
    if (open) {
      setExtractedFields([]);
    }
  }, [open]);

  const FIELD_LABELS = {
    glycemia: 'Glicemie', hba1c: 'HbA1c',
    weight: 'Greutate', hdl: 'HDL', ldl: 'LDL',
    triglycerides: 'Trigliceride', other_lab_results: 'Alte analize'
  };

  // Returns blue border class if field was auto-extracted
  const autoClass = (field) => extractedFields.includes(FIELD_LABELS[field] || '') ? 'border-blue-400 bg-blue-50/50' : '';

  const handleFileSelectAndExtract = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploadingFiles([...uploadingFiles, ...files]);

    // Try to extract lab values from each file
    for (const file of files) {
      const ext = file.name.toLowerCase();
      const isPdfOrImage = ext.endsWith('.pdf') || ext.endsWith('.png') || ext.endsWith('.jpg') || ext.endsWith('.jpeg') || ext.endsWith('.tiff') || ext.endsWith('.bmp');
      if (!isPdfOrImage) continue;

      setExtracting(true);
      try {
        const res = await extractLabValues(file);
        const { values, message } = res.data;
        if (values && Object.keys(values).length > 0) {
          const filled = [];
          const updates = {};
          for (const [key, val] of Object.entries(values)) {
            if (key === 'other_lab_results') {
              const existing = consultationForm.other_lab_results || '';
              updates.other_lab_results = existing ? `${existing}; ${val}` : val;
              filled.push('Alte analize');
            } else if (!consultationForm[key] || consultationForm[key] === '') {
              updates[key] = val;
              filled.push(FIELD_LABELS[key] || key);
            }
          }
          if (Object.keys(updates).length > 0) {
            setConsultationForm(prev => ({ ...prev, ...updates }));
            setExtractedFields(prev => [...prev, ...filled]);
            toast.success(`Valori extrase: ${filled.join(', ')}`, { duration: 5000 });
          } else {
            toast.info(message || 'Valorile deja completate nu au fost suprascrise');
          }
        } else {
          toast.info(message || 'Nu s-au gasit valori in document');
        }
      } catch (err) {
        console.error('Extraction error:', err);
        toast.error('Eroare la extragerea valorilor din document');
      } finally {
        setExtracting(false);
      }
    }
    // Reset input so same file can be re-selected
    e.target.value = '';
  };

  const addMedication = () => {
    if (newMedication.medication_id && (newMedication.morning || newMedication.lunch || newMedication.evening || newMedication.once_weekly)) {
      const medication = medications.find(m => m.id === newMedication.medication_id);
      setConsultationForm({
        ...consultationForm,
        medications: [
          ...consultationForm.medications,
          {
            ...newMedication,
            commercial_name: medication?.commercial || newMedication.commercial_name,
            inn_name: medication?.inn || newMedication.inn_name,
            form: medication?.form || newMedication.form,
            dose: medication?.dose || newMedication.dose,
            ds: newMedication.ds || null,
            treatment_days: newMedication.treatment_days ? parseInt(newMedication.treatment_days) : null,
            once_weekly: newMedication.once_weekly
          }
        ]
      });
      setNewMedication(getInitialMedication());
    } else {
      if (!newMedication.medication_id) {
        toast.error('Selectați un medicament');
      } else if (!newMedication.morning && !newMedication.lunch && !newMedication.evening && !newMedication.once_weekly) {
        toast.error('Selectați cel puțin o opțiune de administrare');
      }
    }
  };

  const removeMedication = (index) => {
    setConsultationForm({
      ...consultationForm,
      medications: consultationForm.medications.filter((_, i) => i !== index)
    });
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setUploadingFiles([...uploadingFiles, ...files]);
  };

  const removeUploadingFile = (index) => {
    setUploadingFiles(uploadingFiles.filter((_, i) => i !== index));
  };

  // Check if there's a pending medication not added
  const hasPendingMedication = newMedication.medication_id && 
    (newMedication.morning || newMedication.lunch || newMedication.evening || newMedication.once_weekly);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate: at least one diagnosis required
    if (!consultationForm.diagnosis_ids || consultationForm.diagnosis_ids.length === 0) {
      toast.error('Selectati cel putin un diagnostic');
      return;
    }
    
    // Warn if there's an unaddded medication
    if (hasPendingMedication) {
      const confirmSave = window.confirm(
        `Aveți un medicament selectat (${newMedication.commercial_name}) care nu a fost adăugat în listă.\n\nDoriți să continuați fără acest medicament?`
      );
      if (!confirmSave) {
        return;
      }
    }
    
    onSubmit(e);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Stethoscope className="w-5 h-5" />
            {isEditing ? 'Editează Consultația' : 'Consultație Nouă'}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Date and Time */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Data consultației *</Label>
              <DatePicker
                value={consultationForm.consultation_date}
                onChange={(date) => setConsultationForm({ ...consultationForm, consultation_date: date })}
                maxDate={format(new Date(), 'yyyy-MM-dd')}
                placeholder="Selectați data"
                data-testid="consultation-date"
              />
            </div>
            <div className="space-y-2">
              <Label>Ora consultației</Label>
              <Input
                type="time"
                value={consultationForm.consultation_time}
                onChange={(e) => setConsultationForm({ ...consultationForm, consultation_time: e.target.value })}
                data-testid="consultation-time"
              />
            </div>
          </div>

          {/* Diagnosis Selector */}
          <DiagnosisSelector
            diagnoses={diagnoses}
            selectedDiagnoses={consultationForm.diagnosis_ids}
            onDiagnosisChange={(ids) => setConsultationForm({ ...consultationForm, diagnosis_ids: ids })}
            diagnosisSearch={diagnosisSearch}
            setDiagnosisSearch={setDiagnosisSearch}
          />

          {/* File Upload - between Diagnoses and Lab Parameters */}
          <div className="space-y-2 p-4 bg-blue-50 rounded-lg border border-blue-100" data-testid="file-upload-section">
            <Label className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-blue-600" />
              Ataseaza documente medicale
            </Label>
            <p className="text-xs text-blue-600 mb-2">
              Uploadeaza analize de laborator (PDF sau imagine) si valorile vor fi completate automat
            </p>
            <div className="flex items-center gap-3">
              <Button 
                type="button" 
                variant="outline"
                className="border-blue-200 hover:bg-blue-100"
                onClick={() => document.getElementById('file-upload').click()}
                disabled={extracting}
                data-testid="upload-file-btn"
              >
                {extracting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4 mr-2" />
                )}
                {extracting ? 'Se analizeaza...' : 'Selecteaza fisiere'}
              </Button>
              <input
                id="file-upload"
                type="file"
                multiple
                accept=".pdf,.png,.jpg,.jpeg,.tiff,.bmp"
                className="hidden"
                onChange={handleFileSelectAndExtract}
              />
              {extractedFields.length > 0 && (
                <span className="flex items-center gap-1 text-xs text-emerald-600">
                  <Check className="w-3.5 h-3.5" />
                  {extractedFields.length} valori importate
                </span>
              )}
            </div>
            {uploadingFiles.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {uploadingFiles.map((file, index) => (
                  <div key={index} className="flex items-center gap-2 p-2 bg-white border border-blue-200 rounded-lg">
                    <FileText className="w-4 h-4 text-blue-500" />
                    <span className="text-sm text-slate-700">{file.name}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 text-slate-400 hover:text-red-500"
                      onClick={() => removeUploadingFile(index)}
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Lab Results */}
          <div className="p-4 bg-slate-50 rounded-lg space-y-4">
            <h4 className="font-medium text-slate-700">Parametri de laborator</h4>
            {extractedFields.length > 0 && (
              <p className="text-xs text-emerald-600 flex items-center gap-1 -mt-2">
                <Check className="w-3.5 h-3.5" />
                Campurile cu bordura verde au fost completate automat din document. Puteti modifica sau sterge valorile.
              </p>
            )}
            <div className="grid grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-1">
                  Glicemie (mg/dL)
                  <span className="text-xs text-slate-400">(N: 70-100)</span>
                </Label>
                <Input
                  type="number"
                  step="0.1"
                  value={consultationForm.glycemia}
                  onChange={(e) => setConsultationForm({ ...consultationForm, glycemia: e.target.value })}
                  className={cn(autoClass('glycemia'), consultationForm.glycemia ? (
                    parseFloat(consultationForm.glycemia) >= 70 && parseFloat(consultationForm.glycemia) <= 100 
                      ? 'border-emerald-500' : 'border-red-500'
                  ) : '')}
                  data-testid="consultation-glycemia"
                />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-1">
                  HbA1c (%)
                  <span className="text-xs text-slate-400">(N: &lt;5.7)</span>
                </Label>
                <Input
                  type="number"
                  step="0.1"
                  value={consultationForm.hba1c}
                  onChange={(e) => setConsultationForm({ ...consultationForm, hba1c: e.target.value })}
                  className={cn(autoClass('hba1c'), consultationForm.hba1c ? (
                    parseFloat(consultationForm.hba1c) <= 5.7 
                      ? 'border-emerald-500' : 'border-red-500'
                  ) : '')}
                  data-testid="consultation-hba1c"
                />
              </div>
              <div className="space-y-2">
                <Label>TA (mmHg)</Label>
                <Input
                  value={consultationForm.blood_pressure}
                  onChange={(e) => setConsultationForm({ ...consultationForm, blood_pressure: e.target.value })}
                  placeholder="120/80"
                  data-testid="consultation-bp"
                />
              </div>
              <div className="space-y-2">
                <Label>Greutate (kg)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={consultationForm.weight}
                  onChange={(e) => setConsultationForm({ ...consultationForm, weight: e.target.value })}
                  data-testid="consultation-weight"
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-1">
                  HDL (mg/dL)
                  <span className="text-xs text-slate-400">(N: &gt;40)</span>
                </Label>
                <Input
                  type="number"
                  step="0.1"
                  value={consultationForm.hdl}
                  onChange={(e) => setConsultationForm({ ...consultationForm, hdl: e.target.value })}
                  className={cn(autoClass('hdl'), consultationForm.hdl ? (
                    parseFloat(consultationForm.hdl) >= 40 
                      ? 'border-emerald-500' : 'border-red-500'
                  ) : '')}
                  data-testid="consultation-hdl"
                />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-1">
                  LDL (mg/dL)
                  <span className="text-xs text-slate-400">(N: &lt;100)</span>
                </Label>
                <Input
                  type="number"
                  step="0.1"
                  value={consultationForm.ldl}
                  onChange={(e) => setConsultationForm({ ...consultationForm, ldl: e.target.value })}
                  className={cn(autoClass('ldl'), consultationForm.ldl ? (
                    parseFloat(consultationForm.ldl) <= 100 
                      ? 'border-emerald-500' : 'border-red-500'
                  ) : '')}
                  data-testid="consultation-ldl"
                />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-1">
                  Trigliceride (mg/dL)
                  <span className="text-xs text-slate-400">(N: &lt;150)</span>
                </Label>
                <Input
                  type="number"
                  step="0.1"
                  value={consultationForm.triglycerides}
                  onChange={(e) => setConsultationForm({ ...consultationForm, triglycerides: e.target.value })}
                  className={cn(autoClass('triglycerides'), consultationForm.triglycerides ? (
                    parseFloat(consultationForm.triglycerides) <= 150 
                      ? 'border-emerald-500' : 'border-red-500'
                  ) : '')}
                  data-testid="consultation-trig"
                />
              </div>
            </div>
            
            {/* Other lab results */}
            <div className="space-y-2">
              <Label>Alte analize</Label>
              <Textarea
                value={consultationForm.other_lab_results}
                onChange={(e) => setConsultationForm({ ...consultationForm, other_lab_results: e.target.value })}
                rows={3}
                placeholder="Alte rezultate de laborator (ex: creatinina, acid uric, hemograma, etc.)"
                className={autoClass('other_lab_results')}
                data-testid="consultation-other-labs"
              />
            </div>
          </div>

          {/* Symptoms & Clinical Exam */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Simptome</Label>
              <Textarea
                value={consultationForm.symptoms}
                onChange={(e) => setConsultationForm({ ...consultationForm, symptoms: e.target.value })}
                rows={3}
                data-testid="consultation-symptoms"
              />
            </div>
            <div className="space-y-2">
              <Label>Examen clinic</Label>
              <Textarea
                value={consultationForm.clinical_exam}
                onChange={(e) => setConsultationForm({ ...consultationForm, clinical_exam: e.target.value })}
                rows={3}
                data-testid="consultation-exam"
              />
            </div>
          </div>

          {/* Medications */}
          <div className="space-y-4">
            <Label className="text-base font-semibold">Medicamente prescrise</Label>
            
            <MedicationSelector
              medications={medications}
              medicationSearch={medicationSearch}
              setMedicationSearch={setMedicationSearch}
              newMedication={newMedication}
              setNewMedication={setNewMedication}
              onAddMedication={addMedication}
            />
            
            {/* Medications list */}
            {consultationForm.medications.length > 0 && (
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="text-left p-3 font-medium">Denumire comercială</th>
                      <th className="text-left p-3 font-medium">DCI</th>
                      <th className="text-left p-3 font-medium">Formă / Doză</th>
                      <th className="text-left p-3 font-medium">DS</th>
                      <th className="text-center p-3 font-medium">Zile</th>
                      <th className="text-center p-3 font-medium w-12">
                        <Sun className="w-4 h-4 mx-auto text-amber-500" />
                      </th>
                      <th className="text-center p-3 font-medium w-12">
                        <Sunset className="w-4 h-4 mx-auto text-orange-500" />
                      </th>
                      <th className="text-center p-3 font-medium w-12">
                        <Moon className="w-4 h-4 mx-auto text-indigo-500" />
                      </th>
                      <th className="text-center p-3 font-medium w-12">
                        <Calendar className="w-4 h-4 mx-auto text-purple-500" />
                      </th>
                      <th className="w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {consultationForm.medications.map((med, index) => (
                      <tr key={index} className="border-t hover:bg-slate-50">
                        <td className="p-3 font-medium">{med.commercial_name}</td>
                        <td className="p-3 text-slate-600" title={med.inn_name}>
                          {med.inn_name?.length > 15 ? `${med.inn_name.substring(0, 15)}...` : med.inn_name}
                        </td>
                        <td className="p-3 text-slate-600" title={`${med.form} - ${med.dose}`}>
                          {`${med.form} - ${med.dose}`.length > 30 
                            ? `${`${med.form} - ${med.dose}`.substring(0, 30)}...` 
                            : `${med.form} - ${med.dose}`}
                        </td>
                        <td className="p-3 text-slate-600">{med.ds || '-'}</td>
                        <td className="p-3 text-center">{med.treatment_days || '-'}</td>
                        <td className="p-3 text-center">
                          {med.morning && <span className="text-emerald-600 font-bold">✓</span>}
                        </td>
                        <td className="p-3 text-center">
                          {med.lunch && <span className="text-emerald-600 font-bold">✓</span>}
                        </td>
                        <td className="p-3 text-center">
                          {med.evening && <span className="text-emerald-600 font-bold">✓</span>}
                        </td>
                        <td className="p-3 text-center">
                          {med.once_weekly && <span className="text-purple-600 font-bold">✓</span>}
                        </td>
                        <td className="p-3">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeMedication(index)}
                            className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Recommendations */}
          <div className="space-y-2">
            <Label>Recomandări</Label>
            <Textarea
              value={consultationForm.recommendations}
              onChange={(e) => setConsultationForm({ ...consultationForm, recommendations: e.target.value })}
              rows={3}
              placeholder="Recomandări pentru pacient, regim alimentar, stilul de viață, etc."
              data-testid="consultation-recommendations"
            />
          </div>

          {/* Submit buttons */}
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => onOpenChange(false)}
            >
              Anulează
            </Button>
            <Button type="submit" data-testid="save-consultation-btn">
              {isEditing ? 'Salvează modificările' : 'Înregistrează consultația'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
